/**
 * MobileBackButton — mobile chrome for global back navigation.
 *
 * Mirrors PreferencesPopout's trigger: a single 44px circular paper chip
 * at top-left with just the "←" arrow. The destination text moves to the
 * aria-label so screen readers still get the full "Back to Five" or
 * "Back to all games" announcement.
 *
 * Destination logic is unchanged — we re-use `backTargetFor()` from the
 * desktop BackToGamesBar so route overrides stay in one place.
 *
 * Only rendered below 768px (the same threshold as PreferencesPopout).
 * Above 768px, the desktop BackToGamesBar renders the full labeled pill.
 *
 * Suppressed when embedded, for the same reason and by the same predicate as
 * <BackToGamesBar />: a tablet-sized host frame is still a host frame, and this
 * is the same door wearing a smaller pill.
 */
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';

import { isEmbedded } from '@/host/isEmbedded';
import { useIsMobileChrome } from '@/preferences/useIsMobileChrome';

import { backTargetFor } from './BackToGamesBar';

import './MobileBackButton.css';

export function MobileBackButton() {
  const isMobile = useIsMobileChrome();
  const location = useLocation();
  const target = backTargetFor(location.pathname);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = (): void => setReducedMotion(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // Embedded: the host is the navigator and supplies its own exit.
  if (isEmbedded()) return null;
  if (!isMobile || !target) return null;

  return (
    <motion.div
      className="mobile-back"
      {...(reducedMotion ? {} : { whileTap: { scale: 0.94 } })}
      transition={
        reducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 500, damping: 28 }
      }
    >
      <Link
        to={target.to}
        className="mobile-back__btn"
        aria-label={`Back to ${target.label}`}
      >
        <span className="mobile-back__arrow" aria-hidden="true">
          {/* SVG arrow — structural UI primitive, not a depiction */}
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M14.5 5.5L8 12l6.5 6.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </Link>
    </motion.div>
  );
}
